import pMemoize from "@-/com.npmjs.p-memoize";
import { Speech } from "@-/com.npmjs.expo-speech";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";

/**
 * Apple's novelty "Eloquence" voices are listed as zh-CN too but sound like 1990s formant
 * synthesis; never pick one for a learner.
 */
const NOVELTY_VOICE_NAMES = new Set([
  "Eddy",
  "Flo",
  "Grandma",
  "Grandpa",
  "Reed",
  "Rocko",
  "Sandy",
  "Shelley",
]);

const MAINLAND_MANDARIN = /^(zh[-_](cn|hans)|cmn)/i;
const STANDARD_VOICE_NAMES = /tingting|lili|yu-shu|li-mu|yue/i;

const isMainlandMandarin = (language: string) => MAINLAND_MANDARIN.test(language);

/**
 * The best installed mainland-Mandarin system voice: an Enhanced/Premium download if the learner
 * has one, otherwise the platform's standard voice (Tingting on iOS) rather than whatever the
 * engine defaults to. `undefined` lets the engine choose (no Mandarin voice listed, or web).
 */
export const pickMandarinSystemVoice = pMemoize(async (): Promise<string | undefined> => {
  const voices = await withDeadline({ deadline: 5000, operation: "list system voices" }, () =>
    Speech.getAvailableVoicesAsync(),
  ).catch(() => []);
  const mandarin = voices.filter(
    (voice) =>
      isMainlandMandarin(voice.language) &&
      !NOVELTY_VOICE_NAMES.has(voice.name.split(" ")[0] ?? ""),
  );
  const best =
    mandarin.find((voice) => voice.quality === "Enhanced") ??
    mandarin.find((voice) => STANDARD_VOICE_NAMES.test(voice.name)) ??
    mandarin[0];
  return best?.identifier;
});
